import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {ComponentEvent, StateAwareComponent} from 'src/app/model/rest/base';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import {PublicV3RestService} from "../../services/rest/v3/public-rest.service";
import {Utils} from "../../services/utils/utils";
import {FileV3RestService} from "../../services/rest/v3/file-rest.service";
import {CountryDefinition, Gender, V3PersonalSensitiveDetails, V3PersonDetails} from "../../model/v3/people";
import {ManagerColV3RestService} from "../../services/rest/v3/manager-col-v3-rest.service";
import {PeopleV3RestService} from "../../services/rest/v3/people-rest.service";
import {V3FileItem} from "../../model/v3/file";
import {combineLatest, concat, Observable, of} from "rxjs";

export class StudentPersonalProfileFormComponentEvent extends ComponentEvent {
  static ET_FORM_EDITABLE = 'underEdit';
  static ET_FORM_READ_ONLY = 'readOnly';

  static fromEditableFlag(editable: boolean) {
    const res = new StudentPersonalProfileFormComponentEvent();
    if (editable) {
      res.eventType = this.ET_FORM_EDITABLE;
    } else {
      res.eventType = this.ET_FORM_READ_ONLY;
    }
    return res;
  }
}

@Component({
    selector: 'app-student-personal-profile-form',
    templateUrl: './student-personal-profile-form.component.html',
    styleUrls: ['./student-personal-profile-form.component.css'],
    standalone: false
})
export class StudentPersonalProfileFormComponent extends StateAwareComponent<StudentPersonalProfileFormComponentEvent> implements OnInit {

  _studentId: number;
  genders = [ 'Male', 'Female', 'Other' ];
  personDetails: V3PersonDetails = {name: '', surname: ''};
  sensitiveDetails: V3PersonalSensitiveDetails = {
    gender: Gender.Other,
    nationality: '',
    birthDate: null,
    biography: '',
    studyReason: '',
  };
  profileEmail = '';
  profilePhoto: V3FileItem | null = null;
  nationalityIso = '';
  countryIso = '';
  personId: number | null = null;
  reasonForStudying = '';
  _editable = true;
  uploadProgress: number = null;

  @ViewChild('fileInput', {static: false}) fileInput;
  countries: CountryDefinition[];
  _schoolId: number;

  constructor(
    private fileV3Rest: FileV3RestService,
    private managerV3Rest: ManagerColV3RestService,
    private peopleV3Rest: PeopleV3RestService,
    publicV3Rest: PublicV3RestService) {
    super();
    publicV3Rest.listCountries().subscribe(countries => this.processCountries(countries));
  }

  processCountries(countries: CountryDefinition[]): void {
    this.countries = countries.concat().sort((l, r) => l.name.localeCompare(r.name));
  }

  @Input()
  set studentId(studentId: number) {
    this._studentId = studentId;
    this.loadProfile();
  }

  @Input()
  set schoolId(schoolId: number) {
    this._schoolId = schoolId;
    this.loadProfile();
  }

  @Input()
  set editable(editable: boolean) {
    this._editable = editable;
    this.stateEvent.emit(StudentPersonalProfileFormComponentEvent.fromEditableFlag(this._editable));
  }

  public openFileChoose() {
    if (!this._editable) {return; }
    this.fileInput.nativeElement.click();
  }

  get editable() {
    return this._editable;
  }

  public getProfilePhoto() {
    if (!this.profilePhoto?.publicAccessUrl) {
      return '/assets/img/user-ml.png';
    }
    return `url(${this.profilePhoto.publicAccessUrl})`;
  }

  public onProfileImageUpload(target: EventTarget) {
    const input = target as HTMLInputElement;
    this.uploadProgress = 0;
    this.fileV3Rest.uploadFile(input.files[0]).subscribe(event => {
      if (event.type === HttpEventType.UploadProgress ) {
        this.uploadProgress = Math.round(100 * event.loaded / event.total);
      } else if (event instanceof HttpResponse) {
        this.profilePhoto = event.body as V3FileItem;
        this.uploadProgress = null;
      }
    }, () => this.uploadProgress = null);
  }

  onBirthDateChange(birthDate: any){
    this.sensitiveDetails.birthDate = birthDate ? new Date(birthDate).getTime() : null;
  }

  saveProfile() {
    if (!this.personId) {
      return of(null);
    }
    const personId = this.personId;
    const nationalityName = this.countries?.find(c => c.code === this.nationalityIso)?.name ?? '';
    const countryName = this.countries?.find(c => c.code === this.countryIso)?.name ?? '';

    const sensitive: V3PersonalSensitiveDetails = {
      gender: this.sensitiveDetails.gender,
      nationality: nationalityName,
      birthDate: this.sensitiveDetails.birthDate,
      biography: this.sensitiveDetails.biography,
      studyReason: this.reasonForStudying,
    };

    // Run the updates one after another (not in parallel) so they don't race. updatePerson is
    // last because it triggers a downstream broadcast of the whole person — running it after the
    // other casa updates ensures the broadcast reflects the final state and doesn't clobber them.
    const orderedUpdates: Observable<unknown>[] = [
      this.peopleV3Rest.updateSensitive(personId, sensitive),
      this.peopleV3Rest.getAddress(personId).pipe(
        switchMap((address) => this.peopleV3Rest.updateAddress(personId, {...address, country: countryName}))
      ),
    ];
    if (this.profilePhoto) {
      orderedUpdates.push(this.peopleV3Rest.updatePhoto(personId, this.profilePhoto));
    }
    orderedUpdates.push(this.peopleV3Rest.updatePerson(personId, {
      name: this.personDetails.name,
      surname: this.personDetails.surname,
    }));

    return concat(...orderedUpdates.map((update) => update.pipe(catchError((err) => {
      console.error('profile update failed', err);
      return of(null);
    }))));
  }

  public onSave() {
    this.editable = false;
    this.saveProfile().subscribe({complete: () => this.loadProfile()});
  }

  loadPerson() {
    return this.managerV3Rest.getStudent(this._schoolId, this._studentId).pipe(
      switchMap((student) =>
        combineLatest([
          of(student),
          this.peopleV3Rest.getSensitive(student.person.id),
          this.peopleV3Rest.getPhoto(student.person.id),
          this.peopleV3Rest.getAddress(student.person.id),
        ])
      ),
      map(([student, sensitive, photo, address]) => {
        const person = student.person;
        return {
          id: person?.id,
          name: person?.details?.name,
          surname: person?.details?.surname,
          email: person?.registrationEmail,
          nationality: sensitive?.nationality,
          gender: sensitive?.gender,
          birthDate: sensitive?.birthDate ? new Date(sensitive.birthDate) : null,
          biography: sensitive?.biography,
          reasonForStuding: sensitive?.studyReason,
          nationalityIso: this.countries?.find(c => c.name === sensitive?.nationality)?.code,
          countryIso: this.countries?.find(c => c.name === address?.country)?.code,
          profilePhoto: photo ?? null
        };
      })
    );
  }

  loadProfile(): any {
    if ( !this._schoolId || !this._studentId) {return; }

    this.loadPerson().pipe(
      map(profile => Utils.jsonClone(profile)),
      tap(profile => this.applyProfile(profile))
    ).subscribe( () => this.stateEvent.emit(ComponentEvent.initialized()));
  }

  ngOnInit() {
    this.editable = false;
  }

  private applyProfile(profile: any): void {
    this.personDetails = {
      name: profile?.name,
      surname: profile?.surname,
    };
    this.profileEmail = profile?.email;
    this.sensitiveDetails = {
      gender: (profile?.gender ?? Gender.Other) as Gender,
      nationality: profile?.nationality,
      birthDate: profile?.birthDate ? new Date(profile.birthDate).getTime() : null,
      biography: profile?.biography,
      studyReason: profile?.reasonForStuding,
    };
    this.reasonForStudying = profile?.reasonForStuding;
    this.personId = profile?.id;
    this.nationalityIso = profile?.nationalityIso;
    this.countryIso = profile?.countryIso;
    this.profilePhoto = profile?.profilePhoto;
  }
}
